/**
 * Provides a debounce function for the given function.
 * @param func
 * @param wait
 * @param immediate
 */
export function debounce(
  func: Function,
  wait: number,
  immediate = false
): () => void {
  let timeout: number | null;

  return function (this: any, ...args) {
    const later = () => {
      timeout = null;
      if (!immediate) {
        func.apply(this, args);
      }
    };
    const callNow = immediate && !timeout;
    if (timeout) {
      window.clearTimeout(timeout);
    }
    timeout = window.setTimeout(later, wait);
    if (callNow) {
      func.apply(this, args);
    }
  };
}

/**
 * Returns a leading+trailing debounced wrapper around `func`.
 *
 * - Leading edge: fires immediately on the first call in a quiet window.
 * - Trailing edge: fires once more after `wait` ms of silence so that any
 *   changes that arrived after the leading fire are flushed.
 *
 * A single isolated call fires twice (leading + trailing). If `func` exits
 * early when there is nothing to do (e.g. no pending changes), the second
 * invocation is a cheap no-op.
 */
export function leadingTrailingDebounce(
  func: (...args: unknown[]) => void,
  wait: number
): (...args: unknown[]) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let leadingFired = false;

  return function (this: unknown, ...args: unknown[]) {
    if (!leadingFired) {
      leadingFired = true;
      func.apply(this, args);
    }

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      timeout = null;
      leadingFired = false;
      func.apply(this, args);
    }, wait);
  };
}
